import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Brain, MessageSquare, FileSearch, TrendingUp, Clock, DollarSign } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState } from "react";
import ContactFormModal from "@/components/ContactFormModal";

export default function AITools() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const features = [
    {
      icon: MessageSquare,
      title: "AI Chatbots",
      description: "24/7 customer support that learns from every conversation"
    },
    {
      icon: FileSearch,
      title: "Document Processing",
      description: "Extract insights from documents automatically"
    },
    {
      icon: Brain,
      title: "Predictive Analytics",
      description: "Forecast trends and make smarter business decisions"
    },
    {
      icon: TrendingUp,
      title: "Lead Scoring",
      description: "Identify your best prospects with AI-powered scoring"
    },
    {
      icon: Clock,
      title: "Process Automation",
      description: "Automate repetitive tasks and free up your team"
    },
    {
      icon: DollarSign,
      title: "Cost Reduction",
      description: "Reduce operational costs by up to 60%"
    },
  ];

  const useCases = [
    {
      title: "Customer Service Automation",
      description: "Deploy AI chatbots that handle common questions, route complex issues to humans, and learn from every interaction."
    },
    {
      title: "Sales Intelligence",
      description: "Analyze customer data to identify patterns, predict buying behavior, and prioritize high-value leads."
    },
    {
      title: "Content Generation",
      description: "Create marketing copy, product descriptions, and social media posts at scale while maintaining your brand voice."
    },
    {
      title: "Data Analysis",
      description: "Turn raw data into actionable insights with AI-powered analytics and visualization tools."
    },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-background" />
          
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-gradient">AI-Powered Tools</span> for Modern Business
            </motion.h1>
            
            <motion.p
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Harness the power of artificial intelligence to automate tasks, gain insights, and scale your operations.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Button
                size="lg"
                onClick={() => setIsContactOpen(true)}
                className="bg-gradient-to-r from-primary to-secondary text-white"
              >
                Explore AI Solutions
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 px-4">
          <div className="container max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              What AI Can Do For You
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="p-6 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <feature.icon className="w-12 h-12 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Real-World Applications
            </h2>
            
            <div className="space-y-6">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={useCase.title}
                  className="p-6 rounded-lg bg-card border border-border"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-2xl font-semibold mb-3">{useCase.title}</h3>
                  <p className="text-muted-foreground text-lg">{useCase.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Why Invest in AI?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
              >
                <div className="text-5xl font-bold text-gradient mb-2">60%</div>
                <p className="text-muted-foreground">Cost Reduction</p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="text-5xl font-bold text-gradient mb-2">24/7</div>
                <p className="text-muted-foreground">Always Available</p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="text-5xl font-bold text-gradient mb-2">10x</div>
                <p className="text-muted-foreground">Faster Processing</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Embrace AI?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's discuss how AI can transform your business operations and drive growth.
            </p>
            <Button
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="bg-gradient-to-r from-primary to-secondary text-white"
            >
              Get Started
            </Button>
          </div>
        </section>
      </div>
      <Footer />
      <ContactFormModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </>
  );
}
